import React, { useCallback, useContext, useEffect, useState } from 'react';

import Skeleton from '@mui/material/Skeleton';
import { t } from 'i18next';

import SelectPlanCard from './sections/SelectPlanCard/SelectPlanCard';
import BdsButton from '../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import ErrorCard from '../../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import { ARROW_RIGHT_ICON } from '../../../../../shared/constants/images';
import { CLICK_ACTION, MANAGE_COLLABORATORS_SELECT_PLAN } from '../../../../../shared/constants/tagManager';
import { PlansContext } from '../../../../../shared/context/plans/plansContext';
import { ICompanyPlan } from '../../../../../shared/interfaces/plans';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../../shared/translations/commons';
import { TRANSLATION_MANAGE_COLLABORATORS } from '../../../../../shared/translations/manageCollaborators';

import './PlanSelection.scss';

interface IPlanSelection {
  selectedPlan?: ICompanyPlan;
  onChangeSelectedPlan: (plan: ICompanyPlan) => void;
  nextStep: () => void;
}

const PlanSelection = ({ selectedPlan, onChangeSelectedPlan, nextStep }: IPlanSelection) => {
  const [selected, setSelected] = useState<ICompanyPlan>();

  const { plans, loading, error, fetchPlans } = useContext(PlansContext);

  const disabledButton = selected === undefined;

  const handleOnChange = (value: ICompanyPlan): void => {
    setSelected(value);
    onChangeSelectedPlan(value);
  };

  useEffect(() => {
    if (selectedPlan && plans) {
      const plan = plans.find((plan) => plan.id === selectedPlan.id);
      setSelected(plan);
    }
  }, [plans, selectedPlan]);

  const renderSkeletons = useCallback(
    () =>
      Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="select-plan-card-skeleton" variant="rectangular" animation={false} />
      )),
    []
  );

  const handleButtonClick = () => {
    TagManagerService.pushEvent(CLICK_ACTION, MANAGE_COLLABORATORS_SELECT_PLAN, {
      id: selected?.commercialPlanId
    });
    nextStep();
  };

  if (loading) {
    return <div className="c-select-plan-selection">{renderSkeletons()}</div>;
  }

  if (error || !plans?.length) {
    return (
      <div className="plan-selection-content flex flex-col justify-center items-center w-full h-full">
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
          action={() => fetchPlans()}
          center={true}
          className="plan-selection-error-card flex flex-col justify-center items-center"
          buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
        />
      </div>
    );
  }

  return (
    <>
      <div className="c-select-plan-selection">
        {plans.map((plan) => (
          <SelectPlanCard
            key={`card-${plan.id}`}
            plan={plan}
            selected={selected?.id === plan.id}
            onChange={() => handleOnChange(plan)}
          />
        ))}
      </div>
      <div className="upload-collaborators-button flex flex-row justify-end">
        <BdsButton
          variant="primary"
          size="l"
          action={handleButtonClick}
          disabled={disabledButton}
          iconRight={ARROW_RIGHT_ICON}
        >
          {t(TRANSLATION_MANAGE_COLLABORATORS.buttonNext)}
        </BdsButton>
      </div>
    </>
  );
};

export default PlanSelection;
